import React from "react"
import { useSearchParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import {
  AlertTriangle,
  CalendarDays,
  Inbox,
  MapPin,
  MessageSquare,
  Paperclip,
  Plus,
  RotateCw,
  Search,
  SlidersHorizontal,
  Tag,
  Trash2,
  UserRound,
  X,
} from "lucide-react"
import { usePolling, getPollingQueryOpts } from "../layout/HoaAdminLayout"
import { useLanguage } from "@/contexts/LanguageContext"
import { listBuildings } from "@/api/buildings"
import {
  useAddIssueComment,
  useCreateIssue,
  useCreateIssueCategory,
  useDeleteIssue,
  useIssue,
  useIssueCategories,
  useIssues,
  useIssueStats,
  useIssueTargets,
  useUpdateIssue,
} from "@/api/issues"
import {
  ISSUES_PAGE_SIZE,
  ISSUE_PRIORITIES,
  ISSUE_STATUSES,
  IssueListItem,
  IssuePriority,
  IssueStatus,
} from "@/types/issue"
import {
  ApiError,
  AttachmentGrid,
  CategoryPicker,
  CategoryWell,
  CommentThread,
  EmptyState,
  FileDrop,
  IssueCardShell,
  IssueListSkeleton,
  MetaList,
  Pager,
  PriorityChip,
  QuietChip,
  SectionHeading,
  StatusChip,
  StatusTimeline,
  TargetPicker,
  TriagePanel,
  type TargetScope,
} from "@/components/issues/IssueBits"
import { useAttachments } from "@/lib/issueAttachments"
import ResponsiveModal from "@/components/common/ResponsiveModal"
import SegmentedTabs from "@/components/common/SegmentedTabs"
import { useIssueLabels, useRelativeAge, type IssueLabels } from "@/lib/issueLabels"
import {
  BRAND,
  PRIORITY_TONE,
  STATUS_TONE,
  isSettled,
  tileStyle,
} from "@/lib/issueStyles"

/** Just enough of a building to name it in a select. */
export type BuildingChoice = { id: string; name: string }

/**
 * The buildings this admin manages.
 *
 * /buildings/ is already scoped by hoa_admin on the server, so this is the
 * caller's own list rather than everything. Shared between the filter and
 * the create form via one query key so switching between them is free.
 */
function useMyBuildings() {
  return useQuery({
    queryKey: ["buildings", "mine"],
    queryFn: listBuildings,
    staleTime: 5 * 60 * 1000,
    select: (data): BuildingChoice[] =>
      data.map((b) => ({ id: b.id, name: b.name })),
  })
}

/* ================================================================== */
export default function IssuesPage() {
  const { enabled, intervalMs } = usePolling()
  const { t } = useLanguage()
  const labels = useIssueLabels()

  const [tab, setTab] = React.useState<"ALL" | IssueStatus>("ALL")
  const [q, setQ] = React.useState("")
  const [search, setSearch] = React.useState("")
  const [priority, setPriority] = React.useState<IssuePriority | "ALL">("ALL")
  const [category, setCategory] = React.useState<string | "ALL">("ALL")
  const [building, setBuilding] = React.useState<string | "ALL">("ALL")
  const [filtersOpen, setFiltersOpen] = React.useState(false)
  const [page, setPage] = React.useState(1)

  const buildings = useMyBuildings()
  const buildingChoices = buildings.data ?? []

  const [creating, setCreating] = React.useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const openId = searchParams.get("issue")
  function setOpenId(id: string | null) {
    const next = new URLSearchParams(searchParams)
    if (id) next.set("issue", id)
    else next.delete("issue")
    setSearchParams(next, { replace: !id })
  }
  const [confirmDelete, setConfirmDelete] = React.useState<IssueListItem | null>(null)

  // Typing must not fire a request per keystroke against a server-side filter.
  React.useEffect(() => {
    const id = setTimeout(() => setSearch(q.trim()), 300)
    return () => clearTimeout(id)
  }, [q])

  // Changing any filter must return to page 1: staying on page 4 of a
  // narrower result set lands you on an empty page, which reads as "no
  // results" when there are plenty.
  React.useEffect(() => {
    setPage(1)
  }, [tab, priority, category, building, search])

  const filters = { status: tab, priority, category, building, search, page }
  const issues = useIssues(filters, getPollingQueryOpts(enabled, intervalMs))
  // Follows the building filter, and ONLY the building filter. The counters
  // describe the whole set so they do not move when you narrow the list --
  // but a three-building admin looking at BLDG-C is not asking about the
  // other two, and a tile reading 40 above a list of 6 is just wrong.
  const stats = useIssueStats(building === "ALL" ? {} : { building })
  const categories = useIssueCategories(building === "ALL" ? undefined : building)

  const updateMutation = useUpdateIssue()
  const deleteMutation = useDeleteIssue()

  const rows = issues.data?.results ?? []
  const total = issues.data?.count ?? 0
  const narrowed =
    priority !== "ALL" || category !== "ALL" || building !== "ALL" || search !== ""
  const activeCategories = (categories.data ?? []).filter((c) => c.is_active)

  function clearFilters() {
    setPriority("ALL")
    setCategory("ALL")
    setBuilding("ALL")
    setQ("")
    setSearch("")
  }

  return (
    <div className="space-y-4">
      {/* ---------- Header ---------- */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-[19px] font-semibold tracking-[-0.01em] text-gray-900 sm:text-[21px]">
            {t("hoa.issues.heading")}
          </h1>
          <p className="mt-0.5 text-[13px] text-gray-500">{t("hoa.issues.subtitle")}</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => issues.refetch()}
            disabled={issues.isFetching}
            aria-label={t("common.refresh")}
            className="grid h-10 w-10 place-items-center rounded-xl bg-white text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]"
          >
            <RotateCw className={`h-4 w-4 ${issues.isFetching ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => setCreating(true)}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl px-3.5 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] focus-visible:ring-offset-2 sm:px-4"
            style={{ background: `linear-gradient(100deg, ${BRAND} 0%, ${BRAND} 55%, #27AE60 160%)` }}
          >
            <Plus className="h-4 w-4" />
            <span className="hidden xs:inline sm:inline">{t("hoa.issues.new")}</span>
          </button>
        </div>
      </header>

      {/* ---------- Stat tiles: also the fastest way to filter ---------- */}
      {stats.data && (
        <div className="-mx-4 flex snap-x gap-2.5 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-4 sm:overflow-visible sm:px-0">
          <StatTile
            label={t("hoa.issues.stat.open")}
            value={stats.data.open}
            accent={BRAND}
            tone={tileStyle(STATUS_TONE.IN_PROGRESS)}
            active={tab === "ALL"}
            onClick={() => setTab("ALL")}
          />
          <StatTile
            label={labels.status.PENDING}
            value={stats.data.by_status.PENDING}
            accent={STATUS_TONE.PENDING.rail}
            tone={tileStyle(STATUS_TONE.PENDING)}
            active={tab === "PENDING"}
            onClick={() => setTab("PENDING")}
          />
          <StatTile
            label={labels.status.RESOLVED}
            value={stats.data.by_status.RESOLVED}
            accent={STATUS_TONE.RESOLVED.rail}
            tone={tileStyle(STATUS_TONE.RESOLVED)}
            active={tab === "RESOLVED"}
            onClick={() => setTab("RESOLVED")}
          />
          <StatTile
            label={labels.priority.URGENT}
            value={stats.data.by_priority.URGENT}
            accent={PRIORITY_TONE.URGENT.rail}
            tone={tileStyle(PRIORITY_TONE.URGENT)}
            active={priority === "URGENT"}
            onClick={() => setPriority(priority === "URGENT" ? "ALL" : "URGENT")}
          />
        </div>
      )}

      {/* ---------- Status tabs ---------- */}
      <SegmentedTabs
        ariaLabel={t("hoa.issues.heading")}
        value={tab}
        onChange={setTab}
        items={(["ALL", ...ISSUE_STATUSES] as const).map((key) => ({
          key,
          label: key === "ALL" ? t("common.all") : labels.status[key as IssueStatus],
          count: key === "ALL" ? stats.data?.total : stats.data?.by_status[key as IssueStatus],
          tone:
            key === "ALL"
              ? { bg: "#E7EBF8", fg: "#1B3480" }
              : { bg: STATUS_TONE[key as IssueStatus].bg, fg: STATUS_TONE[key as IssueStatus].fg },
        }))}
      />

      {/* ---------- Search + filters ---------- */}
      <div className="space-y-2.5">
        <div className="flex gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("hoa.issues.searchPlaceholder")}
              className="h-10 w-full rounded-xl bg-white pl-9 pr-9 text-sm text-gray-900 ring-1 ring-gray-200 transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1F3A93]"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                aria-label={t("common.reset")}
                className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* On a phone the two selects fold away; on desktop they are always out. */}
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            className={`inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl px-3 text-sm font-medium ring-1 transition sm:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] ${
              narrowed
                ? "bg-[#E7EBF8] text-[#1B3480] ring-[#B9C4E8]"
                : "bg-white text-gray-600 ring-gray-200"
            }`}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {narrowed && <span className="h-1.5 w-1.5 rounded-full" style={{ background: BRAND }} />}
          </button>
        </div>

        {/* Three columns once a building filter is in play, two otherwise --
            an admin with one building has nothing to choose between and
            should not be given an empty decision. */}
        <div
          className={`${filtersOpen ? "grid" : "hidden"} gap-2 sm:grid ${
            buildingChoices.length > 1 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"
          }`}
        >
          {buildingChoices.length > 1 && (
            <Select
              value={building}
              onChange={setBuilding}
              allLabel={t("issues.allBuildings")}
              options={buildingChoices.map((b) => ({ value: b.id, label: b.name }))}
            />
          )}
          <Select
            value={priority}
            onChange={(v) => setPriority(v as IssuePriority | "ALL")}
            allLabel={t("hoa.issues.allPriorities")}
            options={ISSUE_PRIORITIES.map((p) => ({ value: p, label: labels.priority[p] }))}
          />
          <Select
            value={category}
            onChange={setCategory}
            allLabel={t("hoa.issues.allCategories")}
            options={activeCategories.map((c) => ({ value: c.id, label: c.name }))}
          />
        </div>

        {narrowed && (
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-800 hover:underline"
          >
            <X className="h-3 w-3" />
            {t("hoa.issues.clearFilters")}
          </button>
        )}
      </div>

      {/* ---------- List ---------- */}
      {issues.isLoading ? (
        <IssueListSkeleton />
      ) : issues.isError ? (
        <EmptyState
          tone="error"
          icon={AlertTriangle}
          title={t("hoa.issues.loadError")}
          action={
            <button
              onClick={() => issues.refetch()}
              className="rounded-xl bg-white px-3 py-1.5 text-xs font-semibold text-red-700 ring-1 ring-red-200 hover:bg-red-50"
            >
              {t("common.refresh")}
            </button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title={narrowed || tab !== "ALL" ? t("hoa.issues.emptyFiltered") : t("hoa.issues.empty")}
          hint={narrowed || tab !== "ALL" ? t("hoa.issues.emptyFilteredHint") : t("hoa.issues.emptyHint")}
          action={
            narrowed || tab !== "ALL" ? (
              <button
                onClick={() => {
                  clearFilters()
                  setTab("ALL")
                }}
                className="rounded-xl bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
              >
                {t("hoa.issues.clearFilters")}
              </button>
            ) : undefined
          }
        />
      ) : (
        <>
          <ul className="space-y-2.5">
            {rows.map((issue) => (
              <IssueCard
                key={issue.id}
                issue={issue}
                labels={labels}
                busy={updateMutation.isPending}
                onQuickStatus={(next) => updateMutation.mutate({ id: issue.id, data: { status: next } })}
                onOpen={() => setOpenId(issue.id)}
                onDelete={() => setConfirmDelete(issue)}
              />
            ))}
          </ul>
          <Pager
            page={page}
            pageSize={ISSUES_PAGE_SIZE}
            count={total}
            onChange={setPage}
            busy={issues.isFetching}
          />
        </>
      )}

      {creating && (
        <CreateIssueModal
          labels={labels}
          buildings={buildingChoices}
          // Pre-selects whatever the list is filtered to: an admin who
          // narrowed to BLDG-C and then hit New is telling you which
          // building they mean.
          initialBuildingId={building === "ALL" ? undefined : building}
          onClose={() => setCreating(false)}
        />
      )}

      {openId && (
        <IssueDetailModal
          key={openId}
          issueId={openId}
          labels={labels}
          onClose={() => setOpenId(null)}
          onDelete={(issue) =>
            setConfirmDelete(rows.find((r) => r.id === issue.id) ?? (issue as IssueListItem))
          }
        />
      )}

      {confirmDelete && (
        <ConfirmDelete
          title={confirmDelete.title}
          busy={deleteMutation.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() =>
            deleteMutation.mutate(confirmDelete.id, {
              onSuccess: () => {
                if (openId === confirmDelete.id) setOpenId(null)
                setConfirmDelete(null)
              },
            })
          }
        />
      )}
    </div>
  )
}

/* ================================================================== */
function StatTile({
  label,
  value,
  accent,
  tone,
  active,
  onClick,
}: {
  label: string
  value: number
  accent: string
  tone: React.CSSProperties
  active?: boolean
  onClick?: () => void
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      // The dashboard's Stat tile: a soft tinted ground with a matching
      // hairline, not a white card with a coloured cap.
      className="min-w-[7.25rem] shrink-0 snap-start rounded-2xl px-3.5 py-3 text-left transition sm:min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]"
      style={{
        ...tone,
        ...(active ? { boxShadow: `inset 0 0 0 2px ${accent}` } : null),
      }}
    >
      <div className="text-[11px] font-medium">{label}</div>
      <div className="mt-0.5 text-2xl font-bold tabular-nums">{value}</div>
    </button>
  )
}

function Select({
  value,
  onChange,
  allLabel,
  options,
}: {
  value: string
  onChange: (v: string) => void
  allLabel: string
  options: { value: string; label: string }[]
}) {
  const isAll = value === "ALL"
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`h-10 w-full rounded-xl px-3 text-sm ring-1 transition focus:outline-none focus:ring-2 focus:ring-[#1F3A93] ${
        isAll ? "bg-white text-gray-600 ring-gray-200" : "bg-[#E7EBF8] font-medium text-[#1B3480] ring-[#B9C4E8]"
      }`}
    >
      <option value="ALL">{allLabel}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

/* ================================================================== */
function IssueCard({
  issue,
  labels,
  busy,
  onQuickStatus,
  onOpen,
  onDelete,
}: {
  issue: IssueListItem
  labels: IssueLabels
  busy: boolean
  onQuickStatus: (next: IssueStatus) => void
  onOpen: () => void
  onDelete: () => void
}) {
  const { t } = useLanguage()
  const settled = isSettled(issue.status)

  const actions: { label: string; to: IssueStatus }[] =
    issue.status === "PENDING"
      ? [
          { label: t("hoa.issues.actions.start"), to: "IN_PROGRESS" },
          { label: t("hoa.issues.actions.resolve"), to: "RESOLVED" },
          { label: t("hoa.issues.actions.cancel"), to: "CANCELLED" },
        ]
      : issue.status === "IN_PROGRESS"
      ? [
          { label: t("hoa.issues.actions.markResolved"), to: "RESOLVED" },
          { label: t("hoa.issues.actions.backToPending"), to: "PENDING" },
        ]
      : [{ label: t("hoa.issues.actions.reopen"), to: "PENDING" }]

  return (
    <li>
      <IssueCardShell status={issue.status} settled={settled}>
      <div className="px-3.5 sm:px-4">
        {/* Tapping anywhere on the body opens it; the buttons below sit outside. */}
        <button
          onClick={onOpen}
          className="flex w-full items-start gap-3 py-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] focus-visible:ring-inset sm:py-4"
        >
          <CategoryWell icon={issue.category_icon} status={issue.status} />

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="min-w-0 text-[15px] font-semibold leading-snug text-gray-900 line-clamp-2 sm:text-base">
                {issue.title}
              </h3>
              <StatusChip status={issue.status} labels={labels} size="sm" />
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <PriorityChip priority={issue.priority} labels={labels} />
              <span className="text-gray-300" aria-hidden>·</span>
              <span className="text-[11px] text-gray-500">
                {issue.category_name ?? "—"}
              </span>
              <QuietChip>
                {issue.unit_code ?? t("issues.commonArea")}
              </QuietChip>
              {issue.attachment_count > 0 && (
                <QuietChip icon={Paperclip}>{issue.attachment_count}</QuietChip>
              )}
              {issue.comment_count > 0 && (
                <QuietChip icon={MessageSquare}>{issue.comment_count}</QuietChip>
              )}
            </div>

            <p className="mt-2 text-[11px] text-gray-500">
              {t("hoa.issues.updatedBy", {
                date: new Date(issue.updated_at).toLocaleDateString(),
                name: issue.reporter_name ?? "—",
              })}
            </p>
          </div>
        </button>

        {/* Quick actions. Horizontally scrollable so three fit on a phone. */}
        <div className="-mx-3.5 flex items-center gap-1.5 overflow-x-auto border-t border-black/[0.06] px-3.5 py-2 sm:-mx-4 sm:px-4">
          {actions.map((a) => (
            <button
              key={a.to}
              disabled={busy}
              onClick={() => onQuickStatus(a.to)}
              className="shrink-0 rounded-lg px-2.5 py-1.5 text-[12px] font-medium text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]"
            >
              {a.label}
            </button>
          ))}
          <button
            onClick={onDelete}
            aria-label={t("common.delete")}
            className="ml-auto grid h-8 w-8 shrink-0 place-items-center rounded-lg text-gray-400 transition hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      </IssueCardShell>
    </li>
  )
}

/* ================================================================== */
function CreateIssueModal({
  labels,
  buildings,
  initialBuildingId,
  onClose,
}: {
  labels: IssueLabels
  buildings: BuildingChoice[]
  initialBuildingId?: string
  onClose: () => void
}) {
  const { t } = useLanguage()
  const createMutation = useCreateIssue()

  const [title, setTitle] = React.useState("")
  const [description, setDescription] = React.useState("")
  const [categoryId, setCategoryId] = React.useState<string | null>(null)
  const [priority, setPriority] = React.useState<IssuePriority>("MEDIUM")

  // An admin who manages one building never sees the selector, but the value
  // is still sent. The server used to fall back to `user.building_id` -- one
  // column, stamped once -- so a three-building admin filed against whichever
  // building they were first attached to.
  const [buildingId, setBuildingId] = React.useState<string>(
    initialBuildingId ?? (buildings.length ? buildings[0].id : "")
  )

  // The list may still have been loading when this mounted. Fill in the
  // default once it arrives rather than leaving the form permanently
  // unsubmittable with an empty building.
  React.useEffect(() => {
    if (!buildingId && buildings.length) setBuildingId(buildings[0].id)
  }, [buildings, buildingId])

  const [scope, setScope] = React.useState<TargetScope>("COMMON")
  const [unitId, setUnitId] = React.useState<string | null>(null)

  const attachments = useAttachments()

  const targets = useIssueTargets(buildingId || undefined, !!buildingId)
  const categories = useIssueCategories(buildingId || undefined)
  const createCategory = useCreateIssueCategory(buildingId || undefined)
  const activeCategories = (categories.data ?? []).filter((c) => c.is_active)

  // Default to the building's General once the list arrives, rather than
  // leaving nothing selected and relying on the server's fallback.
  React.useEffect(() => {
    if (categoryId || activeCategories.length === 0) return
    const general = activeCategories.find((c) => c.key === "GENERAL")
    setCategoryId((general ?? activeCategories[0]).id)
  }, [activeCategories, categoryId])

  // Switching building invalidates both the chosen unit and the chosen
  // category -- they are rows belonging to the building you just left, and
  // the server rejects both with a 403 that would read as a bug.
  function changeBuilding(next: string) {
    setBuildingId(next)
    setUnitId(null)
    setScope("COMMON")
    setCategoryId(null)
  }

  const titleOk = title.trim().length >= 3
  const descOk = description.trim().length >= 10
  // "A specific unit" with no unit chosen is the one state that must not be
  // submittable: it used to be indistinguishable from "common area".
  const targetOk = scope === "COMMON" || !!unitId
  const canSubmit = titleOk && descOk && targetOk && !!buildingId

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    createMutation.mutate(
      {
        title: title.trim(),
        description: description.trim(),
        category: categoryId,
        priority,
        unit: scope === "UNIT" ? unitId : null,
        building: buildingId,
        attachments: attachments.files,
      },
      { onSuccess: onClose }
    )
  }

  return (
    <ResponsiveModal
      open
      onClose={onClose}
      title={t("hoa.issues.modal.newTitle")}
      headerAccent={BRAND}
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-11 flex-1 rounded-xl bg-white text-sm font-medium text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="new-issue"
            disabled={createMutation.isPending || !canSubmit}
            className="h-11 flex-[2] rounded-xl text-sm font-semibold text-white shadow-sm transition active:scale-[0.99] disabled:opacity-50"
            style={{ background: `linear-gradient(100deg, ${BRAND} 0%, ${BRAND} 55%, #27AE60 160%)` }}
          >
            {createMutation.isPending ? t("common.saving") : t("common.save")}
          </button>
        </div>
      }
    >
      <form id="new-issue" onSubmit={submit} className="space-y-4">
        {/* Building first: it scopes the units, the residents and the
            categories below it. Hidden entirely for the common case of one
            building -- a select with a single option is a decision nobody
            has to make. */}
        {buildings.length > 1 && (
          <Field label={t("issues.building")} required>
            <select
              value={buildingId}
              onChange={(e) => changeBuilding(e.target.value)}
              className={inputClass}
            >
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label={t("common.title")} required>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("hoa.issues.titlePlaceholder")}
            className={inputClass}
          />
        </Field>

        <Field label={t("issues.description")} required>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            placeholder={t("hoa.issues.descriptionPlaceholder")}
            className={`${inputClass} min-h-[6rem] resize-y py-2.5`}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label={t("issues.category")}>
              <CategoryPicker
                categories={activeCategories}
                value={categoryId}
                onChange={setCategoryId}
                loading={categories.isLoading}
                canCreate
                creating={createCategory.isPending}
                onCreate={(name, icon) =>
                  createCategory.mutate(
                    { name, icon },
                    { onSuccess: (created) => setCategoryId(created.id) }
                  )
                }
              />
              <ApiError error={createCategory.error} />
            </Field>
          </div>

          <Field label={t("common.priority")}>
            {/* Segmented, not a dropdown: priority is the field an admin
                changes most and it is worth one tap. */}
            <div className="flex gap-1 rounded-xl bg-gray-100/80 p-1">
              {ISSUE_PRIORITIES.map((p) => {
                const active = priority === p
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={`flex-1 rounded-lg py-1.5 text-[11px] font-semibold transition ${
                      active ? "shadow-sm" : "text-gray-600 hover:text-gray-900"
                    }`}
                    style={
                      active
                        ? { background: PRIORITY_TONE[p].bg, color: PRIORITY_TONE[p].fg }
                        : undefined
                    }
                  >
                    {labels.priority[p]}
                  </button>
                )
              })}
            </div>
          </Field>
        </div>

        <Field label={t("hoa.issues.assignUnit")} required>
          <TargetPicker
            units={targets.data ?? []}
            scope={scope}
            unitId={unitId}
            onChange={(nextScope, nextUnit) => {
              setScope(nextScope)
              setUnitId(nextUnit)
            }}
            loading={targets.isLoading}
          />
          {scope === "UNIT" && !unitId && !targets.isLoading && (
            <p className="mt-1.5 text-[12px] text-gray-500">{t("issues.pickAUnit")}</p>
          )}
          <ApiError error={targets.error} />
        </Field>

        <Field label={t("issues.attachments")}>
          <FileDrop
            files={attachments.files}
            error={attachments.error}
            onAdd={attachments.add}
            onRemove={attachments.remove}
          />
        </Field>

        <ApiError error={createMutation.error} />
      </form>
    </ResponsiveModal>
  )
}

/* ================================================================== */
function IssueDetailModal({
  issueId,
  labels,
  onClose,
  onDelete,
}: {
  issueId: string
  labels: IssueLabels
  onClose: () => void
  onDelete: (issue: { id: string; title: string }) => void
}) {
  const { t } = useLanguage()
  const detail = useIssue(issueId)
  const updateMutation = useUpdateIssue()
  const commentMutation = useAddIssueComment()
  const categories = useIssueCategories()
  const relativeAge = useRelativeAge()
  const detailCategories = (categories.data ?? []).filter((c) => c.is_active)

  const [note, setNote] = React.useState("")
  const [comment, setComment] = React.useState("")
  const [isInternal, setIsInternal] = React.useState(false)

  const issue = detail.data

  function setStatus(next: IssueStatus) {
    if (!issue || next === issue.status) return
    updateMutation.mutate(
      { id: issue.id, data: { status: next, status_note: note.trim() } },
      { onSuccess: () => setNote("") }
    )
  }

  return (
    <ResponsiveModal
      open
      onClose={onClose}
      headerAccent={issue ? STATUS_TONE[issue.status].rail : undefined}
      title={issue?.title ?? t("common.loading")}
      subtitle={
        issue ? (
          <span className="flex flex-wrap items-center gap-2">
            <StatusChip status={issue.status} labels={labels} size="sm" />
            <PriorityChip priority={issue.priority} labels={labels} />
            <span className="text-gray-400">{relativeAge(issue.created_at)}</span>
          </span>
        ) : undefined
      }
      footer={
        issue ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onDelete({ id: issue.id, title: issue.title })}
              className="inline-flex h-11 items-center gap-1.5 rounded-xl px-3.5 text-sm font-medium text-red-700 ring-1 ring-red-200 transition hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              <span className="hidden sm:inline">{t("common.delete")}</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="h-11 flex-1 rounded-xl bg-white text-sm font-medium text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50"
            >
              {t("common.close")}
            </button>
          </div>
        ) : undefined
      }
    >
      {detail.isError ? (
        <ApiError error={detail.error} />
      ) : !issue ? (
        <IssueListSkeleton rows={2} />
      ) : (
        <div className="space-y-5">
          {/* What an admin needs before deciding anything: where, who, when. */}
          <div className="flex items-start gap-3 rounded-2xl bg-gray-50/80 p-3.5 ring-1 ring-gray-200/70">
            <CategoryWell icon={issue.category_icon} status={issue.status} />
            <div className="min-w-0 flex-1">
              <MetaList
                items={[
                  {
                    label: t("common.unit"),
                    value: issue.unit_code ?? t("issues.commonArea"),
                    icon: MapPin,
                  },
                  {
                    label: t("issues.category"),
                    value: issue.category_name ?? "—",
                    icon: Tag,
                  },
                  {
                    label: t("issues.reporter"),
                    value: issue.reporter_name ?? "—",
                    icon: UserRound,
                  },
                  {
                    label: t("issues.reported"),
                    value: new Date(issue.created_at).toLocaleDateString(),
                    icon: CalendarDays,
                  },
                ]}
              />
            </div>
          </div>

          <section>
            <SectionHeading>{t("common.description")}</SectionHeading>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-700">
              {issue.description}
            </p>
          </section>

          {issue.attachments.length > 0 && (
            <section>
              <SectionHeading>{t("issues.attachments")}</SectionHeading>
              <AttachmentGrid attachments={issue.attachments} />
            </section>
          )}

          <TriagePanel
            status={issue.status}
            note={note}
            onNote={setNote}
            onStatus={setStatus}
            busy={updateMutation.isPending}
            labels={labels}
          >
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <label className="block">
                <span className="text-[11px] font-medium text-gray-600">
                  {t("common.priority")}
                </span>
                <select
                  value={issue.priority}
                  onChange={(e) =>
                    updateMutation.mutate({
                      id: issue.id,
                      data: { priority: e.target.value as IssuePriority },
                    })
                  }
                  className={`${inputClass} mt-1`}
                  style={{
                    background: PRIORITY_TONE[issue.priority].bg,
                    color: PRIORITY_TONE[issue.priority].fg,
                  }}
                >
                  {ISSUE_PRIORITIES.map((p) => (
                    <option key={p} value={p}>{labels.priority[p]}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-[11px] font-medium text-gray-600">
                  {t("issues.category")}
                </span>
                <select
                  value={issue.category ?? ""}
                  onChange={(e) =>
                    updateMutation.mutate({
                      id: issue.id,
                      data: { category: e.target.value },
                    })
                  }
                  className={`${inputClass} mt-1`}
                >
                  {/* An issue filed under a category that has since been
                      retired keeps showing it, so the option has to exist. */}
                  {issue.category && !detailCategories.some((c) => c.id === issue.category) && (
                    <option value={issue.category}>{issue.category_name}</option>
                  )}
                  {detailCategories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>
            </div>

            <ApiError error={updateMutation.error} />
          </TriagePanel>

          <StatusTimeline changes={issue.status_changes} labels={labels} />

          <CommentThread
            comments={issue.comments}
            value={comment}
            onValue={setComment}
            busy={commentMutation.isPending}
            error={commentMutation.error}
            canMarkInternal
            isInternal={isInternal}
            onIsInternal={setIsInternal}
            onSubmit={() =>
              commentMutation.mutate(
                { id: issue.id, body: comment.trim(), isInternal },
                { onSuccess: () => setComment("") }
              )
            }
          />
        </div>
      )}
    </ResponsiveModal>
  )
}

/* ================================================================== */
function ConfirmDelete({
  title,
  busy,
  onCancel,
  onConfirm,
}: {
  title: string
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useLanguage()
  return (
    <div className="fixed inset-0 z-[60] grid place-items-end sm:place-items-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-[2px]" onClick={onCancel} aria-hidden />
      <div className="relative w-full rounded-t-3xl bg-white p-5 shadow-2xl sm:w-[calc(100%-3rem)] sm:max-w-sm sm:rounded-2xl">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-red-50 text-red-600">
            <Trash2 className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold text-gray-900">
              {t("hoa.issues.deleteTitle")}
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              {t("hoa.issues.deleteMessage", { title })}
            </p>
            <p className="mt-1.5 text-[11px] text-gray-500">{t("hoa.issues.deleteFiles")}</p>
          </div>
        </div>
        <div className="mt-5 flex gap-2 pb-[max(0rem,env(safe-area-inset-bottom))]">
          <button
            onClick={onCancel}
            className="h-11 flex-1 rounded-xl bg-white text-sm font-medium text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="h-11 flex-1 rounded-xl bg-red-600 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? t("common.working") : t("common.delete")}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ================================================================== */
const inputClass =
  "h-10 w-full rounded-xl bg-white px-3 text-sm text-gray-900 ring-1 ring-gray-200 transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1F3A93]"

function Field({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {children}
    </label>
  )
}
